import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { showToast } from "../../utils/customToast";
import { SECURITY_KEY } from "../../globals";
import "../Master/Ledger/UpdateLedger.css";
import { apiWithLog } from "../../utils/apiWithLog";

// Helper to generate correct logo URL
const getLogoUrl = (fileName) => {
  if (!fileName || fileName === "null") return null;
  const asString = fileName.toString().trim();
  if (/^https?:\/\//i.test(asString)) return asString;

  let cleanedPath = asString.replace(/^~[\\/]+/, "");
  cleanedPath = cleanedPath.replace(/\\/g, "/");
  cleanedPath = cleanedPath.replace(/^\/+/, "");
  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;
  const restApiRoot = BASE_REST_API_URL.replace(/\/+$/, "");
  const uploadRoot = restApiRoot.replace(/\/restapi$/i, "");

  if (/^uploads\//i.test(cleanedPath)) {
    return `${uploadRoot}/${cleanedPath}`;
  }

  return `${restApiRoot}/${cleanedPath}`;
};

// Helper to generate company code from first letters
const generateCompanyCode = (name) => {
  if (!name) return "";
  const words = name
    .trim()
    .split(/[\s\-_&,./\\|()]+/)
    .filter(Boolean);
  return words.map((w) => w[0].toUpperCase()).join("");
};

const CompUpdateData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const initialData = location.state?.responseData || {};

  const [editableData, setEditableData] = useState(() => ({
    ...initialData,
    CompanyCode: initialData.CompanyCode || (initialData.Name ? generateCompanyCode(initialData.Name) : ""),
    CompanyColourCode: initialData.CompanyColourCode || "#000000",
  }));
  const [previewImage, setPreviewImage] = useState(
    getLogoUrl(initialData.LogoPath),
  );
  const [stateList, setStateList] = useState([]);
  const [stateCodeMap, setStateCodeMap] = useState({});

  const [loading, setLoading] = useState(false);

  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  // Fetch state list on component mount
  useEffect(() => {
    const fetchStates = async () => {
      const apiUrl = `${BASE_REST_API_URL}/SelectData.aspx`;
      const data = new URLSearchParams();
      data.append("SecurityKey", SECURITY_KEY);
      data.append("TableName", "gststates");
      data.append("WhereCondition", "All");
      data.append("*", "*");

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: data.toString(),
        });
        const result = await response.json();

        if (Array.isArray(result.Response)) {
          setStateList(result.Response);

          const map = {};
          result.Response.forEach((state) => {
            map[state.ID] = state.ID.toString().padStart(2, "0");
          });
          setStateCodeMap(map);
        } else {
          showToast.error("No states found in API response.");
        }
      } catch (err) {
        console.error("Error fetching states:", err);
        showToast.error("Error fetching state list.");
      }
    };

    fetchStates();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "LogoPath") {
      if (files && files.length > 0) {
        const file = files[0];
        setEditableData((prevData) => ({ ...prevData, [name]: file }));
        setPreviewImage(URL.createObjectURL(file));
      }
    } else if (name === "Name") {
      setEditableData((prevData) => ({
        ...prevData,
        Name: value,
        CompanyCode: prevData.CompanyCode ? prevData.CompanyCode : generateCompanyCode(value),
      }));
    } else if (name === "CompanyCode") {
      setEditableData((prevData) => ({
        ...prevData,
        CompanyCode: value.toUpperCase(),
      }));
    } else if (name === "CompanyColourCode") {
      setEditableData((prevData) => ({
        ...prevData,
        CompanyColourCode: value,
      }));
    } else if (name === "GSTNO" || name === "PanNo" || name === "IFSCCode") {
      setEditableData((prevData) => ({
        ...prevData,
        [name]: value.toUpperCase(),
      }));
    } else {
      setEditableData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleRemoveLogo = () => {
    setPreviewImage(null);
    setEditableData((prevData) => ({
      ...prevData,
      LogoPath: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isGSTValid = () => {
    if (!editableData.GSTNO || !editableData.StateID) return true;
    const firstTwoGST = editableData.GSTNO.substring(0, 2);
    const selectedStateId = stateCodeMap[editableData.StateID];
    return firstTwoGST === selectedStateId;
  };

  const handleSaveChanges = async () => {
    if (loading) return;
    if (!editableData.CompID || !editableData.Name) {
      showToast.warn("CompID and Name are required.");
      return;
    }

    // ===== GST Validation =====
    if (editableData.GSTNO && editableData.GSTNO.length !== 15) {
      showToast.error("GST No must be exactly 15 characters!");
      return;
    }

    if (!isGSTValid()) {
      showToast.error(
        "GST No first two digits do not match the selected state's ID!"
      );
      return;
    }

    // ===== Prepare FormData =====
    const formData = new FormData();
    formData.append("SecurityKey", SECURITY_KEY);
    formData.append("TableName", "company");
    formData.append("WhereCondition", `CompID=${editableData.CompID}`);

    Object.entries(editableData).forEach(([key, value]) => {
      if (key !== "CompID" && value !== undefined && value !== null) {
        if (key === "LogoPath" && value === "null") {
          formData.append(key, "");
        } else {
          formData.append(key, value);
        }
      }
    });

    const apiUrl = `${BASE_REST_API_URL}/updatedata.aspx`;

    try {
      setLoading(true);

      // ===== API Call with Logging =====
      const res = await apiWithLog({
        method: "POST",
        url: apiUrl,
        data: formData, // FormData (file/logo supported)
        module: "Company",
        action: "E",
      });

      // ===== Handle ASP.NET Response =====
      let result = res?.data || res;

      if (typeof result === "string") {
        try {
          result = JSON.parse(result);
        } catch {
          console.log("Text response:", result);
          showToast.save("Company updated successfully!");
          navigate("/selectcompny", {
            state: { updatedItem: editableData },
          });
          return;
        }
      }

      console.log("Company Update Response:", result);

      if (result?.Response?.[0]?.Status === "Ok" || result?.success) {
        showToast.save("Company updated successfully!");
        navigate("/selectcompny", {
          state: { updatedItem: editableData },
        });
      } else {
        showToast.error("Update failed: " + (result?.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Update error:", error);

      // Even if response format issue, assume success (ASP.NET behavior)
      showToast.warn("Update successful, but response format unknown.");
      navigate("/selectcompny", {
        state: { updatedItem: editableData },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update_ledger_container">
      {/* Header & Breadcrumbs */}
      <div className="update_ledger_header_bar">
        <div className="update_ledger_title_group">
          <h4 className="update_ledger_title">Update Company</h4>
          <div className="update_ledger_breadcrumbs">
            <Link to="/home">Home</Link>
            <span>/</span>
            <Link to="/selectcompny">Company</Link>
            <span>/</span>
            <span>Update</span>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="update_ledger_card">
        <div className="update_ledger_card_header">
          <h5>Company Information</h5>
        </div>

        <form encType="multipart/form-data" onSubmit={(e) => { e.preventDefault(); handleSaveChanges(); }}>
          <div className="update_ledger_grid">
            {/* Name */}
            <div className="update_ledger_field">
              <label htmlFor="Name">
                Company Name <span className="required">*</span>
              </label>
              <input
                id="Name"
                type="text"
                name="Name"
                value={editableData.Name || ""}
                onChange={handleInputChange}
                placeholder="e.g. Acme Tours & Travels"
                required
              />
            </div>

            {/* Company Code */}
            <div className="update_ledger_field">
              <label htmlFor="CompanyCode">
                Company Code
              </label>
              <input
                id="CompanyCode"
                type="text"
                name="CompanyCode"
                value={editableData.CompanyCode || ""}
                onChange={handleInputChange}
                placeholder="e.g. DTT"
                style={{ textTransform: "uppercase", fontWeight: "600", letterSpacing: "1px" }}
              />
            </div>

            {/* Company Color Code */}
            <div className="update_ledger_field">
              <label htmlFor="CompanyColourCode">
                Company Color Code (Default: Black)
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  id="CompanyColourCodePicker"
                  type="color"
                  name="CompanyColourCode"
                  value={editableData.CompanyColourCode || "#000000"}
                  onChange={handleInputChange}
                  style={{
                    width: "44px",
                    height: "38px",
                    padding: "2px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    cursor: "pointer",
                    background: "#fff",
                  }}
                  title="Pick Color"
                />
                <input
                  id="CompanyColourCode"
                  type="text"
                  name="CompanyColourCode"
                  value={editableData.CompanyColourCode || ""}
                  onChange={handleInputChange}
                  placeholder="#000000"
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            {/* Address */}
            <div className="update_ledger_field">
              <label htmlFor="Address">
                Address <span className="required">*</span>
              </label>
              <input
                id="Address"
                type="text"
                name="Address"
                value={editableData.Address || ""}
                onChange={handleInputChange}
                placeholder="Full Address"
                required
              />
            </div>

            {/* Tel No */}
            <div className="update_ledger_field">
              <label htmlFor="TelNo">Telephone Number</label>
              <input
                id="TelNo"
                type="text"
                name="TelNo"
                value={editableData.TelNo || ""}
                onChange={handleInputChange}
                placeholder="e.g. +91 9876543210"
              />
            </div>

            {/* GST No */}
            <div className="update_ledger_field">
              <label htmlFor="GSTNO">GST No (15 digits)</label>
              <input
                id="GSTNO"
                type="text"
                name="GSTNO"
                value={editableData.GSTNO || ""}
                onChange={handleInputChange}
                placeholder="e.g. 08ABCDE1234F1Z5"
                style={{ textTransform: "uppercase" }}
              />
            </div>

            {/* State Dropdown */}
            <div className="update_ledger_field">
              <label htmlFor="StateID">
                State <span className="required">*</span>
              </label>
              <select
                id="StateID"
                name="StateID"
                value={editableData.StateID || ""}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Select State --</option>
                {stateList
                  .sort((a, b) => a.State.localeCompare(b.State))
                  .map((state) => (
                    <option key={state.ID} value={state.ID}>
                      {state.State} ({state.ID})
                    </option>
                  ))}
              </select>
            </div>

            {/* PAN No */}
            <div className="update_ledger_field">
              <label htmlFor="PanNo">PAN No</label>
              <input
                id="PanNo"
                type="text"
                name="PanNo"
                value={editableData.PanNo || ""}
                onChange={handleInputChange}
                placeholder="e.g. ABCDE1234F"
                style={{ textTransform: "uppercase" }}
              />
            </div>

            {/* Bank Name */}
            <div className="update_ledger_field">
              <label htmlFor="BankName">Bank Name</label>
              <input
                id="BankName"
                type="text"
                name="BankName"
                value={editableData.BankName || ""}
                onChange={handleInputChange}
                placeholder="e.g. State Bank of India"
              />
            </div>

            {/* Bank Account No */}
            <div className="update_ledger_field">
              <label htmlFor="BankAccountNo">Bank Account No</label>
              <input
                id="BankAccountNo"
                type="text"
                name="BankAccountNo"
                value={editableData.BankAccountNo || ""}
                onChange={handleInputChange}
                placeholder="Account Number"
              />
            </div>

            {/* IFSC Code */}
            <div className="update_ledger_field">
              <label htmlFor="IFSCCode">IFSC Code</label>
              <input
                id="IFSCCode"
                type="text"
                name="IFSCCode"
                value={editableData.IFSCCode || ""}
                onChange={handleInputChange}
                placeholder="e.g. SBIN0001234"
                style={{ textTransform: "uppercase" }}
              />
            </div>

            {/* Logo Upload */}
            <div className="update_ledger_field">
              <label htmlFor="LogoPath">Company Logo</label>
              <input
                ref={fileInputRef}
                id="LogoPath"
                type="file"
                name="LogoPath"
                accept="image/*"
                onChange={handleInputChange}
              />
              {previewImage && (
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <img
                    src={previewImage}
                    alt="Company Logo"
                    style={{
                      width: "70px",
                      height: "70px",
                      objectFit: "contain",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "4px",
                      background: "#fff",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#dc2626",
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    title="Remove Company Logo"
                  >
                    <i className="fa-solid fa-trash-can"></i> Remove Logo
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="update_ledger_actions">
            <Link to="/selectcompny" className="update_ledger_cancel_btn">
              Cancel
            </Link>
            <button
              type="submit"
              className="update_ledger_submit_btn"
              disabled={loading}
            >
              {loading ? "Updating..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompUpdateData;
